/**
 * Tab-transition performance instrumentation — Phase 1 measurement.
 *
 * OPT-IN. Zero overhead when disabled. Enable by either:
 *   - localStorage.setItem('tabPerf', '1')
 *   - Add ?tabPerf=1 to the URL once (the flag is then read at runtime)
 *
 * Instruments the user's tab-switch journey across five marks:
 *   1. tap          — user tapped a navbar tab (BottomNav handlers)
 *   2. chunkReady   — webpack chunk imported (or already cached)
 *   3. routeMount   — React Router committed the new route
 *   4. apiStart     — first axios request after the route mounted
 *   5. apiEnd       — that request's response received
 *   6. paint        — first browser paint after routeMount (rAF)
 *
 * After ~1.5s, the per-tap session summarizes itself in the console:
 *   [tabPerf] /member/carte (chunk cold)
 *   ┌──────────────────────────┬─────────┐
 *   │ tap → chunkReady         │ 423ms   │
 *   │ chunkReady → routeMount  │ 18ms    │
 *   │ routeMount → paint       │ 12ms    │
 *   │ routeMount → apiStart    │ 14ms    │
 *   │ apiStart → apiEnd        │ 287ms   │
 *   │ TOTAL tap → paint        │ 453ms   │
 *   └──────────────────────────┴─────────┘
 *
 * The dominant row IS the bottleneck. We treat:
 *   - (chunk cold) tap → chunkReady > 200ms → preload the chunk
 *   - apiStart → apiEnd > 200ms → cache via useDataCache or skeleton screen
 *   - routeMount → paint > 100ms → component mount cost (rare)
 *
 * Production safety: the marks are no-ops when disabled. Each mark
 * gracefully ignores missing predecessors so a half-completed transition
 * doesn't throw.
 */

const sessions = new Map(); // pathname → session object
const chunkFirstLoad = new Map(); // pathname → ms timestamp of first lazy resolve

let _enabledCache = null;
const enabled = () => {
  if (_enabledCache !== null) return _enabledCache;
  try {
    const fromLs = typeof localStorage !== 'undefined' && localStorage.getItem('tabPerf') === '1';
    const fromQs =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('tabPerf') === '1';
    // If query param is present, persist so it survives the next nav
    if (fromQs && typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('tabPerf', '1');
      } catch (_) {}
    }
    _enabledCache = fromLs || fromQs;
  } catch (_) {
    _enabledCache = false;
  }
  return _enabledCache;
};

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const ms = (n) => `${Math.round(n)}ms`;

const ensureSession = (path) => {
  let s = sessions.get(path);
  if (!s) {
    s = { path };
    sessions.set(path, s);
  }
  return s;
};

/** Called by the navbar tap handler the moment the user taps a tab. */
export const markTap = (path) => {
  if (!enabled() || !path) return;
  // New transition for this path → fresh session (replace any stale data
  // from a previous tap to the same tab).
  sessions.set(path, { path, tap: now() });
};

/** Called from the lazy() wrapper when the import promise resolves. */
export const markChunkLoaded = (path) => {
  if (!enabled() || !path) return;
  if (!chunkFirstLoad.has(path)) chunkFirstLoad.set(path, now());
};

/** Called by <TabPerfTracker> on every React Router location change. */
export const markRouteMount = (path) => {
  if (!enabled() || !path) return;
  const s = ensureSession(path);
  s.routeMount = now();
};

export const markPaint = (path) => {
  if (!enabled() || !path) return;
  const s = ensureSession(path);
  if (!s.paint) s.paint = now();
};

/** Called from services/api.js axios interceptors. */
export const markApiStart = (path) => {
  if (!enabled() || !path) return;
  const s = ensureSession(path);
  // Only capture the FIRST API call after route mount — subsequent ones
  // are usually parallel/secondary and not what blocks first paint.
  if (!s.apiStart && s.routeMount) s.apiStart = now();
};

export const markApiEnd = (path) => {
  if (!enabled() || !path) return;
  const s = ensureSession(path);
  if (s.apiStart && !s.apiEnd) s.apiEnd = now();
};

/**
 * Print the breakdown for `path` to the console.
 * Called ~1.5s after route mount so any in-flight API call has time
 * to record its end timestamp.
 */
export const summarize = (path) => {
  if (!enabled() || !path) return;
  const s = sessions.get(path);
  if (!s || !s.tap) return;

  const isCached = chunkFirstLoad.has(path) && chunkFirstLoad.get(path) < s.tap;
  const chunkReadyAt = isCached ? s.tap : chunkFirstLoad.get(path);

  const fmt = (a, b) => (a != null && b != null ? ms(b - a) : '—');
  const totalToPaint = s.paint ? ms(s.paint - s.tap) : '—';
  const totalToData = s.apiEnd ? ms(s.apiEnd - s.tap) : '—';

  const breakdown = {
    'tap → chunkReady': isCached
      ? '0ms (cached)'
      : chunkReadyAt
      ? fmt(s.tap, chunkReadyAt)
      : '—',
    'chunkReady → routeMount': fmt(chunkReadyAt, s.routeMount),
    'routeMount → paint': fmt(s.routeMount, s.paint),
    'routeMount → apiStart': fmt(s.routeMount, s.apiStart),
    'apiStart → apiEnd': fmt(s.apiStart, s.apiEnd),
    'TOTAL tap → paint': totalToPaint,
    'TOTAL tap → data': totalToData,
  };

  // eslint-disable-next-line no-console
  console.log(
    `%c[tabPerf] ${path} ${isCached ? '(chunk cached)' : '(chunk cold)'}`,
    'color: #9398DE; font-weight: bold; font-size: 12px',
  );
  // eslint-disable-next-line no-console
  console.table(breakdown);

  // Free the slot so the next tap to the same path starts fresh
  sessions.delete(path);
};
